import { useMemo, useState } from "react";
import {
  LifebuoyIcon,
  ClockIcon,
  ChatsCircleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";

import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import {
  useHelpRequests,
  useHelpRequest,
  useRespondHelp,
  useSetHelpStatus,
  useAssignHelp,
  useInternalAdmins,
} from "@/client/services/help/help";
import type { HelpRequest, HelpRequest as HelpRequestType } from "@/client/services/help/help";
import { useDialog } from "@/client/stores/dialog";
import { useUser } from "@/client/services/user";
import { DataTable } from "../_components/datatables";
import type { Column } from "../_components/datatables";
import { KpiCard } from "../_components/kpi-card";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CENTRE D'AIDE — file d'attente admin (style agent workspace)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Le triage en un coup d'œil : âge coloré (SLA), statut, assignation.
 *  Un clic ouvre le fil de conversation : réponse, note interne, statut,
 *  assignation nominative (plusieurs admins simultanés).
 * ─────────────────────────────────────────────────────────────────────────────
 */

const CATEGORY_LABELS: Record<HelpRequest["category"], string> = {
  DOWNLOAD_EXPORT: "Téléchargement",
  PAYMENT: "Paiement",
  ACCOUNT_SECURITY: "Compte & sécurité",
  AI_FEATURE: "Fonctions IA",
  OTHER: "Autre",
};

const STATUS_BADGES: Record<
  HelpRequest["status"],
  { label: string; variant?: "warning" | "info" | "success"; className?: string }
> = {
  OPEN: { label: "Ouverte", variant: "warning" },
  IN_PROGRESS: { label: "En cours", variant: "info" },
  RESOLVED: { label: "Résolue", variant: "success" },
  // Étiquette neutre : tokens admin (gris moyen sur gris clair) — la variante
  // "secondary" de la lib UI rend un texte blanc sur fond clair (illisible).
  CLOSED: {
    label: "Clôturée",
    className: "border-0 bg-admin-surface-hover text-admin-text-secondary",
  },
};

/** Âge de la demande — coloration SLA : rouge > 24 h, ambre > 4 h. */
const ageInfo = (createdAt: string) => {
  const hours = (Date.now() - new Date(createdAt).getTime()) / 3600000;
  if (hours >= 24) return { label: `${Math.floor(hours / 24)} j`, urgent: true, warn: false };
  if (hours >= 4) return { label: `${Math.floor(hours)} h`, urgent: false, warn: true };
  if (hours < 1)
    return { label: `${Math.max(1, Math.round(hours * 60))} min`, urgent: false, warn: false };
  return { label: `${Math.floor(hours)} h`, urgent: false, warn: false };
};

export const HelpAdminPage = () => {
  const [status, setStatus] = useState<string>("OPEN,IN_PROGRESS");
  const [category, setCategory] = useState<string>("all");

  const activeStatus =
    status === "all"
      ? undefined
      : status === "OPEN,IN_PROGRESS"
        ? undefined
        : (status as HelpRequestType["status"]);
  const activeCategory = category === "all" ? undefined : (category as HelpRequestType["category"]);

  const { requests, loading } = useHelpRequests(activeStatus, activeCategory);

  // Filtre local : statut composite « à traiter » (OPEN + IN_PROGRESS)
  const visible = useMemo(() => {
    if (!requests) return [];
    if (status === "OPEN,IN_PROGRESS")
      return requests.filter((r) => r.status === "OPEN" || r.status === "IN_PROGRESS");
    return requests;
  }, [requests, status]);

  const stats = useMemo(() => {
    const all = requests ?? [];
    const openCount = all.filter((r) => r.status === "OPEN").length;
    const inProgress = all.filter((r) => r.status === "IN_PROGRESS").length;
    const slaBreached = all.filter(
      (r) =>
        !r.firstResponseAt &&
        (r.status === "OPEN" || r.status === "IN_PROGRESS") &&
        ageInfo(r.createdAt).urgent,
    ).length;
    const resolved7d = all.filter(
      (r) => r.resolvedAt && Date.now() - new Date(r.resolvedAt).getTime() < 7 * 86400000,
    ).length;
    return { openCount, inProgress, slaBreached, resolved7d };
  }, [requests]);

  const { open: openDetail } = useDialog<{ id: string }>("help-detail");

  const columns: Column<HelpRequestType>[] = [
    {
      key: "code",
      header: "Réf.",
      render: (item) => (
        <span className="font-mono text-xs font-semibold text-admin-text-primary">{item.code}</span>
      ),
    },
    {
      key: "subject",
      header: "Sujet",
      render: (item) => (
        <div className="max-w-[320px]">
          <p className="truncate text-sm font-medium text-admin-text-primary">{item.subject}</p>
          <p className="mt-0.5 line-clamp-1 text-xs text-admin-text-muted">{item.message}</p>
        </div>
      ),
    },
    {
      key: "category",
      header: "Thème",
      render: (item) => (
        <Badge className="border-0 bg-admin-surface-hover text-admin-text-secondary">
          {CATEGORY_LABELS[item.category]}
        </Badge>
      ),
    },
    {
      key: "user",
      header: "Client",
      render: (item) => (
        <div>
          <p className="text-sm text-admin-text-primary">{item.user?.name ?? "—"}</p>
          <p className="text-xs text-admin-text-muted">{item.user?.email}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (item) => {
        const badge = STATUS_BADGES[item.status];
        return (
          <Badge variant={badge.variant} className={badge.className}>
            {badge.label}
          </Badge>
        );
      },
    },
    {
      key: "createdAt",
      header: "Âge",
      align: "center",
      render: (item) => {
        const age = ageInfo(item.createdAt);
        return (
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs font-medium",
              age.urgent
                ? "text-red-600"
                : age.warn
                  ? "text-amber-600"
                  : "text-admin-text-secondary",
            )}
          >
            <ClockIcon size={12} />
            {age.label}
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-admin-text-primary">Centre d'aide</h1>
          <p className="mt-1 text-sm text-admin-text-secondary">
            Les demandes des clients — chaque demande sans réponse est un client en attente.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          icon={<LifebuoyIcon size={18} />}
          label="Ouvertes"
          value={stats.openCount}
          tone="amber"
        />
        <KpiCard
          icon={<ChatsCircleIcon size={18} />}
          label="En cours"
          value={stats.inProgress}
          tone="sky"
        />
        <KpiCard
          icon={<ClockIcon size={18} />}
          label="SLA dépassé"
          value={stats.slaBreached}
          tone="red"
        />
        <KpiCard
          icon={<CheckCircleIcon size={18} />}
          label="Résolues (7 j)"
          value={stats.resolved7d}
          tone="emerald"
        />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[220px] border-admin-border bg-admin-background text-admin-text-secondary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="OPEN,IN_PROGRESS">À traiter (ouvertes + en cours)</SelectItem>
            <SelectItem value="OPEN">Ouvertes</SelectItem>
            <SelectItem value="IN_PROGRESS">En cours</SelectItem>
            <SelectItem value="RESOLVED">Résolues</SelectItem>
            <SelectItem value="CLOSED">Clôturées</SelectItem>
            <SelectItem value="all">Toutes</SelectItem>
          </SelectContent>
        </Select>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[200px] border-admin-border bg-admin-background text-admin-text-secondary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les thèmes</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2 text-sm text-admin-text-muted">
          <MagnifyingGlassIcon size={14} />
          {visible.length} demande{visible.length > 1 ? "s" : ""}
        </div>
      </div>

      {/* File d'attente */}
      <DataTable
        title=""
        data={visible}
        columns={columns}
        pageSize={10}
        loading={loading}
        className="shadow-sm-admin"
        onRowClick={(item) => openDetail("read", { id: "help-detail", item: { id: item.id } })}
      />
    </div>
  );
};
